/*
 * cart-pin-stamp.js — marca las líneas del carrito con el pin del cliente.
 *
 * El checkout de Shopify no ve los atributos del carrito cuando le pide
 * tarifas a un servicio de transporte: solo recibe la dirección escrita y las
 * líneas con sus propiedades. Por eso la clasificación del pin viaja también
 * como propiedades de línea con prefijo de guion bajo: ocultas para el cliente
 * en el checkout, visibles en el pedido dentro del panel (donde le sirven al
 * mensajero) y disponibles para el servicio de tarifas.
 *
 * El estampado ocurre en dos momentos:
 *   1. al ir al checkout, en las cuatro puertas del tema, y
 *   2. al agregar al carrito, cuando ya hay una ubicación confirmada, para que
 *      una línea nueva no quede sin marca y se separe de las demás.
 *
 * Al ir al checkout cada línea lleva además su tipo de entrega, que el servicio
 * usa para decidir qué métodos ofrecer (templates/cart.cv-envio.liquid):
 *   _cv_tipo         immediate | xela | backorder | slowback | amazon | marketplace | neutro
 *   _cv_bodega       gt | xela | ambas: tienda que puede despacharla con mensajero
 *   _cv_marketplace  '1' solo en marketplace: el pedido va solo por Cargo Expreso
 *   _cv_dias_pedido  días hábiles bajo pedido, solo en backorder, slowback y amazon
 * Estas claves describen el producto, no la ubicación: no se borran con el pin y se
 * estampan aunque no haya ubicación, porque marketplace va por Cargo Expreso sin pin
 * (contrato MARKETPLACE-BAJO-PEDIDO-THEME.md del repo del servicio).
 *
 * Y el tipo de cliente, que decide qué métodos ve (Panel → Tipos de cliente del servicio):
 *   _cv_cliente      'mayorista' si el cliente tiene la etiqueta mayorista, mayoristac o
 *                    mayorista c (snippets/cv-tipo-cliente.liquid, en #cv-coverage-config
 *                    como customerType). A un minorista se le quita: sin la clave el
 *                    servicio lo trata como minorista.
 *
 * Y el método que el cliente eligió en el carrito (shipping-quote.js), con el que la
 * caja ofrece solo ese:
 *   _cv_envio        '<kind>:<code>' (mensajero:UBER12, cargo_expreso:CARGO_EXPRESO,
 *                    recoger_tienda:RECOGER_GT). Solo lo pasan el carrito lateral, /cart
 *                    y Comprar ahora (stampCartLines({ envio })); en cualquier otra
 *                    estampada se quita, para que una elección vieja no se quede pegada.
 *                    No va al agregar al carrito.
 *
 * Nunca rechaza la promesa ni bloquea el checkout: si algo falla, la línea se
 * queda sin marca, el servicio no ofrece mensajero y el cliente ve Cargo
 * Expreso.
 */
(function() {
  'use strict';

  var LAT_KEY = '_cv_lat';
  var LNG_KEY = '_cv_lng';
  var METHOD_KEY = '_cv_metodo';
  var ZONE_KEY = '_cv_zona';
  var DEBUG_KEY = '_cv_debug';
  var CUSTOMER_KEY = '_cv_cliente';
  var CHOICE_KEY = '_cv_envio';
  var WHOLESALE = 'mayorista';
  var KEYS = [LAT_KEY, LNG_KEY, METHOD_KEY, ZONE_KEY];
  var TYPE_KEY = '_cv_tipo';
  var STORE_KEY = '_cv_bodega';
  var MARKETPLACE_KEY = '_cv_marketplace';
  var LEAD_DAYS_KEY = '_cv_dias_pedido';
  var TYPE_KEYS = [TYPE_KEY, STORE_KEY, MARKETPLACE_KEY, LEAD_DAYS_KEY];
  var LEAD_TYPES = { backorder: true, slowback: true, amazon: true };
  // Se estampan primero las líneas que más restringen el envío: si se agota el
  // presupuesto, las que quedan sin tipo cuentan como stock de Guatemala.
  var TYPE_PRIORITY = { marketplace: 0, xela: 1, backorder: 2, slowback: 2, amazon: 2 };

  /*
   * Marca de pruebas del servicio de tarifas. El servicio puede devolver
   * tarifas experimentales solo cuando la línea trae esta propiedad, así que
   * quien previsualiza un tema con el ajuste encendido las ve y el cliente de
   * la tienda publicada no. El valor es por tema: encenderlo en un tema sin
   * publicar no afecta a nadie más.
   */
  var configCache = null;

  function coverageConfig() {
    if (configCache !== null) return configCache;
    configCache = {};
    try {
      if (typeof document === 'undefined' || typeof document.getElementById !== 'function') return configCache;
      var node = document.getElementById('cv-coverage-config');
      if (!node) return configCache;
      configCache = JSON.parse(node.textContent || node.innerText || '{}') || {};
    } catch (e) {
      configCache = {};
    }
    return configCache;
  }

  function debugEnabled() {
    return coverageConfig().debugRates === true;
  }

  // El tipo de cliente se calcula en Liquid al cargar la página, con la sesión de ese momento.
  function isWholesale() {
    return coverageConfig().customerType === WHOLESALE;
  }

  // Tope de espera antes de seguir al checkout sin marca. Perder el mensajero
  // es preferible a dejar al cliente mirando un botón que no responde.
  var BUDGET_MS = 8000;
  var MAX_LINES = 60;

  function localeRoot() {
    return (window.theme && window.theme.localeRootUrl) || '';
  }

  function coverageState() {
    try {
      var api = window.CelovendoCoverage;
      if (api && typeof api.getState === 'function') return api.getState();
    } catch (e) {}
    return null;
  }

  function cookieLocation() {
    try {
      var match = document.cookie.match(/(?:^|;\s*)cv_location=([^;]*)/);
      if (!match) return null;
      return JSON.parse(decodeURIComponent(match[1]));
    } catch (e) {
      return null;
    }
  }

  function isPrecise(location) {
    return !!(location && typeof location.lat === 'number' && typeof location.lng === 'number' &&
      location.confirmed === true && location.precise !== false);
  }

  /*
   * Devuelve las propiedades a estampar, o null si todavía no hay una
   * ubicación confirmada. El servicio de tarifas vuelve a clasificar el punto
   * por su cuenta; método y zona viajan solo como referencia para el pedido.
   *
   * La cookie `cv_location` es un recurso de último momento, no una segunda
   * opinión. El motor arranca en revisión 0 y solo pasa de ahí cuando alguien
   * fija o invalida un destino, así que:
   *   - revisión 0 (o motor ausente): nadie se ha pronunciado en esta página
   *     —el caso de "Comprar ahora" desde una ficha de producto—, y la cookie
   *     es la mejor información disponible.
   *   - revisión mayor que 0: el motor ya habló y su palabra manda. Cuando el
   *     cliente empieza a mover el pin, la compuerta del mapa invalida el
   *     destino pero la cookie conserva el anterior; hacerle caso a la cookie
   *     ahí estamparía el punto que el cliente está abandonando, y el checkout
   *     ofrecería mensajero por una ubicación que ya no es la suya.
   */
  function getStamp() {
    var state = coverageState();
    var authoritative = !!(state && state.revision);
    var location = state && state.location;
    var coverage = (state && state.coverage) || {};
    if (!isPrecise(location)) {
      if (authoritative) return null;
      location = cookieLocation();
      coverage = {};
      if (!isPrecise(location)) return null;
    }
    var stamp = {};
    stamp[LAT_KEY] = String(location.lat);
    stamp[LNG_KEY] = String(location.lng);
    stamp[METHOD_KEY] = String(coverage.delivery_method || location.delivery_method || '');
    stamp[ZONE_KEY] = String(coverage.delivery_zone || location.zone || '');
    if (debugEnabled()) stamp[DEBUG_KEY] = '1';
    // Al agregar al carrito solo se agrega; quitarla a un minorista queda para el checkout.
    if (isWholesale()) stamp[CUSTOMER_KEY] = WHOLESALE;
    return stamp;
  }

  function isEmpty(value) {
    return value === undefined || value === null || value === '';
  }

  // Un valor null en la marca pide que la clave no exista en la línea.
  function sameStamp(properties, stamp) {
    var current = properties || {};
    var keys = Object.keys(stamp);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (stamp[key] === null) {
        if (!isEmpty(current[key])) return false;
        continue;
      }
      if (String(isEmpty(current[key]) ? '' : current[key]) !== stamp[key]) return false;
    }
    // Con el ajuste apagado, una línea que quedó marcada de una prueba anterior
    // tiene que perder la marca: si no, seguiría recibiendo tarifas de prueba.
    if (!stamp[DEBUG_KEY] && current[DEBUG_KEY]) return false;
    return true;
  }

  function merged(properties, stamp) {
    var result = {};
    var source = properties || {};
    Object.keys(source).forEach(function(key) {
      if (source[key] !== null && source[key] !== undefined) result[key] = source[key];
    });
    Object.keys(stamp).forEach(function(key) {
      if (stamp[key] === null) delete result[key];
      else result[key] = stamp[key];
    });
    if (!stamp[DEBUG_KEY]) delete result[DEBUG_KEY];
    return result;
  }

  // cvInternal: los cambios del propio estampado no avisan cv:cart-mutated.
  function postJSON(path, payload) {
    return fetch(localeRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      cvInternal: true
    }).then(function(response) {
      if (!response.ok) throw new Error(path + ' respondió ' + response.status);
      return response.json();
    });
  }

  /* ------------------------------------------------------------------ *
   * Tipo de entrega por variante, desde /cart?view=cv-envio.
   * ------------------------------------------------------------------ */

  // Sin "Accept: application/json": con ese encabezado Shopify ignora ?view= y
  // responde el JSON normal del carrito (sin "lines"), y el carrito parecía vacío.
  function fetchCartEnvio() {
    return fetch(localeRoot() + '/cart?view=cv-envio&_=' + Date.now(), {
      credentials: 'same-origin',
      cvInternal: true
    }).then(function(response) {
      if (!response.ok) throw new Error('cv-envio respondió ' + response.status);
      return response.text();
    }).then(function(text) {
      var data = JSON.parse(text);
      if (!data || !Array.isArray(data.lines)) throw new Error('cv-envio sin líneas');
      return data;
    });
  }

  // Por variante y no por clave: la clave cambia con cada cambio de propiedades.
  function typesByVariant(data) {
    if (!data || !Array.isArray(data.lines)) return null;
    var types = {};
    data.lines.forEach(function(line) {
      if (line && line.variantId !== undefined && line.variantId !== null) types[String(line.variantId)] = line;
    });
    return types;
  }

  function typeFor(item, types) {
    if (!types || !item) return null;
    var id = item.variant_id !== undefined ? item.variant_id : item.id;
    return types[String(id)] || null;
  }

  // La marca de una línea: el pin, el tipo de cliente y el tipo de su variante, si se conoce.
  function stampFor(item, pinStamp, types) {
    var stamp = {};
    Object.keys(pinStamp).forEach(function(key) { stamp[key] = pinStamp[key]; });
    stamp[CUSTOMER_KEY] = isWholesale() ? WHOLESALE : null;
    var entry = typeFor(item, types);
    if (!entry) return stamp;
    var tipo = String(entry.tipo || '');
    var days = parseInt(entry.dias, 10);
    stamp[TYPE_KEY] = tipo;
    stamp[STORE_KEY] = String(entry.bodega || '');
    stamp[MARKETPLACE_KEY] = tipo === 'marketplace' ? '1' : null;
    stamp[LEAD_DAYS_KEY] = LEAD_TYPES[tipo] && days > 0 ? String(days) : null;
    return stamp;
  }

  function priority(item, types) {
    var entry = typeFor(item, types);
    var tipo = entry && entry.tipo;
    return tipo && TYPE_PRIORITY.hasOwnProperty(tipo) ? TYPE_PRIORITY[tipo] : 3;
  }

  function firstUnstamped(cart, stamp, types) {
    var items = (cart && cart.items) || [];
    var best = null;
    var bestRank = Infinity;
    for (var i = 0; i < items.length; i++) {
      if (sameStamp(items[i].properties, stampFor(items[i], stamp, types))) continue;
      var rank = priority(items[i], types);
      if (rank < bestRank) {
        best = items[i];
        bestRank = rank;
      }
    }
    return best;
  }

  /*
   * El mismo JSON para quien cotiza en la tienda (shipping-quote.js). Se
   * recuerda unos segundos y se olvida en cuanto el carrito cambia. Resuelve
   * null si no se pudo leer.
   */
  var ENVIO_TTL_MS = 5000;
  var envioMemo = null;

  function cartEnvio(fresh) {
    var now = Date.now();
    if (!fresh && envioMemo && now - envioMemo.at < ENVIO_TTL_MS) return envioMemo.promise;
    var promise = fetchCartEnvio().catch(function() { return null; });
    envioMemo = { at: now, promise: promise };
    return promise;
  }

  // Propiedades sin ninguna clave del pin, para borrar una marca vieja.
  function stripStamp(properties) {
    var result = {};
    Object.keys(properties || {}).forEach(function(key) {
      if (KEYS.indexOf(key) === -1 && key !== DEBUG_KEY && key !== CHOICE_KEY) result[key] = properties[key];
    });
    return result;
  }

  function firstStamped(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var properties = items[i].properties || {};
      if (properties[LAT_KEY] !== undefined || properties[LNG_KEY] !== undefined) return items[i];
    }
    return null;
  }

  function clearRemaining(cart, done) {
    var item = firstStamped(cart);
    if (!item || done >= MAX_LINES) return Promise.resolve({ ok: !item, cleared: done });
    return postJSON('/cart/change.js', {
      id: item.key,
      quantity: item.quantity,
      properties: stripStamp(item.properties)
    }).then(function(next) {
      return clearRemaining(next, done + 1);
    });
  }

  /*
   * Cambiar las propiedades de una línea puede fusionarla con otra idéntica y
   * mover las claves de las demás, así que se avanza línea por línea usando el
   * carrito que devuelve cada cambio en lugar de una lista tomada al inicio.
   */
  function stampRemaining(cart, stamp, types, done) {
    var item = firstUnstamped(cart, stamp, types);
    if (!item || done >= MAX_LINES) return Promise.resolve({ ok: !item, stamped: done });
    return postJSON('/cart/change.js', {
      id: item.key,
      quantity: item.quantity,
      properties: merged(item.properties, stampFor(item, stamp, types))
    }).then(function(next) {
      return stampRemaining(next, stamp, types, done + 1);
    });
  }

  function withBudget(promise) {
    return new Promise(function(resolve) {
      var settled = false;
      var timer = setTimeout(function() {
        if (settled) return;
        settled = true;
        resolve({ ok: false, reason: 'tiempo-agotado' });
      }, BUDGET_MS);
      promise.then(function(result) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      }, function(error) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve({ ok: false, reason: (error && error.message) || 'error' });
      });
    });
  }

  function fetchCart() {
    return fetch(localeRoot() + '/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cvInternal: true
    }).then(function(response) {
      if (!response.ok) throw new Error('/cart.js respondió ' + response.status);
      return response.json();
    });
  }

  /*
   * Una marca vieja es peor que ninguna marca: si el cliente movió el pin
   * fuera del perímetro y el estampado nuevo no se pudo aplicar, el carrito
   * seguiría diciendo que está adentro y el checkout ofrecería mensajero por
   * una ubicación que ya no es la suya. Por eso, cuando no se puede escribir
   * el pin nuevo —o cuando ya no hay ubicación confirmada— se intenta borrar
   * el viejo. Sin marca, el servicio no ofrece mensajero y el cliente ve Cargo
   * Expreso, que es la salida segura.
   *
   * Si esa limpieza tampoco se puede escribir, o si se agota el presupuesto de
   * espera, el carrito conserva la marca vieja. Es el único hueco que queda y
   * exige que dos escrituras seguidas al carrito fallen.
   */
  function clearWork() {
    return fetchCart().then(function(cart) { return clearRemaining(cart, 0); });
  }

  function clearAfterFailure(reason) {
    return clearWork().then(function(cleared) {
      return { ok: false, reason: reason, limpiadas: cleared.cleared };
    }, function() {
      return { ok: false, reason: reason, limpiadas: 0 };
    });
  }

  // Sin ubicación: la marca pide borrar el pin viejo y conserva la de pruebas.
  function pinlessStamp() {
    var stamp = {};
    KEYS.forEach(function(key) { stamp[key] = null; });
    stamp[DEBUG_KEY] = debugEnabled() ? '1' : null;
    return stamp;
  }

  /*
   * Estampa todas las líneas del carrito. Siempre resuelve. Si no se puede
   * leer el tipo de las líneas, estampa solo el pin, como antes. Sin ubicación
   * borra el pin viejo y estampa igual el tipo.
   * opts.envio: el método elegido ('<kind>:<code>'); sin él se quita _cv_envio.
   * Va también sin ubicación (Comprar ahora de un consumidor final: 'cargo_expreso:').
   */
  function stampCartLines(opts) {
    var pin = getStamp();
    var stamp = pin || pinlessStamp();
    var envio = opts && typeof opts.envio === 'string' ? opts.envio.trim() : '';
    stamp[CHOICE_KEY] = envio || null;
    var work = Promise.all([fetchCart(), cartEnvio(true)]).then(function(results) {
      return stampRemaining(results[0], stamp, typesByVariant(results[1]), 0);
    }).then(function(result) {
      if (pin) return result;
      return { ok: false, reason: 'sin-ubicacion', limpiadas: result.stamped };
    }).catch(function(error) {
      return clearAfterFailure(pin ? ((error && error.message) || 'error') : 'sin-ubicacion');
    });
    return withBudget(work);
  }

  /* ------------------------------------------------------------------ *
   * Estampado al agregar al carrito.
   *
   * El tema agrega productos desde muchos lugares (PDP, upsells del side
   * cart, recomendaciones, wizard de diagnóstico), todos por fetch a
   * /cart/add.js. Envolver fetch una sola vez cubre todos esos caminos y los
   * que se agreguen después, en vez de repetir la misma marca en cada uno.
   * ------------------------------------------------------------------ */

  function isCartAdd(url) {
    return typeof url === 'string' && url.split('?')[0].indexOf('/cart/add') !== -1;
  }

  function isCartMutation(url) {
    return typeof url === 'string' && /\/cart\/(add|change|update|clear)(\.js)?$/.test(url.split('?')[0]);
  }

  /*
   * Aviso para quien muestra precios de envío: el carrito cambió. Sale cuando
   * la escritura responde bien; las del propio estampado no avisan.
   */
  function notifyCartMutated() {
    envioMemo = null;
    try {
      if (typeof document === 'undefined' || typeof document.dispatchEvent !== 'function') return;
      if (typeof CustomEvent !== 'function') return;
      document.dispatchEvent(new CustomEvent('cv:cart-mutated'));
    } catch (e) {}
  }

  function decorateFormData(body, stamp) {
    Object.keys(stamp).forEach(function(key) { body.set('properties[' + key + ']', stamp[key]); });
    return body;
  }

  function decorateJSON(text, stamp) {
    var payload = JSON.parse(text);
    if (payload && Array.isArray(payload.items)) {
      payload.items.forEach(function(item) { item.properties = merged(item.properties, stamp); });
    } else if (payload && typeof payload === 'object') {
      payload.properties = merged(payload.properties, stamp);
    }
    return JSON.stringify(payload);
  }

  function decorateBody(body, stamp) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return decorateFormData(body, stamp);
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      Object.keys(stamp).forEach(function(key) { body.set('properties[' + key + ']', stamp[key]); });
      return body;
    }
    if (typeof body === 'string') {
      var trimmed = body.trim();
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') return decorateJSON(body, stamp);
      var params = new URLSearchParams(body);
      Object.keys(stamp).forEach(function(key) { params.set('properties[' + key + ']', stamp[key]); });
      return params.toString();
    }
    return body;
  }

  function installAddToCartStamp() {
    if (typeof window.fetch !== 'function' || window.__cvPinStampInstalled) return;
    window.__cvPinStampInstalled = true;
    var nativeFetch = window.fetch;
    window.fetch = function(input, init) {
      var mutation = false;
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = String((init && init.method) || (typeof input === 'object' && input && input.method) || 'GET').toUpperCase();
        mutation = method === 'POST' && isCartMutation(url) && !(init && init.cvInternal);
        if (method === 'POST' && isCartAdd(url) && init && init.body) {
          var stamp = getStamp();
          if (stamp) {
            var decorated = decorateBody(init.body, stamp);
            if (decorated !== init.body) {
              var copy = {};
              Object.keys(init).forEach(function(key) { copy[key] = init[key]; });
              copy.body = decorated;
              init = copy;
            }
          }
        }
      } catch (e) {
        // Una marca fallida nunca debe impedir que el producto se agregue.
      }
      var request = nativeFetch.call(this, input, init);
      if (mutation && request && typeof request.then === 'function') {
        request.then(function(response) {
          if (response && response.ok) notifyCartMutated();
        }, function() {});
      }
      return request;
    };
  }

  installAddToCartStamp();

  window.CVPinStamp = {
    getStamp: getStamp,
    stampCartLines: stampCartLines,
    cartEnvio: cartEnvio,
    keys: KEYS,
    typeKeys: TYPE_KEYS,
    customerKey: CUSTOMER_KEY,
    isWholesale: isWholesale
  };
})();
